//
// CallsStore
//
// Provide services for audio calls
//
//
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useReducer,
  useMemo,
} from 'react';

import { useIsMounted } from '../../components/React';
import {
  config,
  displayJson,
  logError,
  isoDatetimeUtc,
} from '../../utils';
import useAuth from '../auth/useAuth';

import CallsContext from './CallsContext';

const initialState = {
  initStarted: false,
  ready: false,
  commandPending: false,
  // ongoing call:
  active: null,
};

const stateReducer = (log) => function stateReducerModel(state, action) {
  if (log) console.log(`CallsStore ${log} stateReducer action=${displayJson(action)}`);
  switch (action.type) {
    case 'initStart': {
      // console.log(`callsStore stateReducer ${displayJson(action)}`);
      if (state.initStarted) return state;
      return {
        ...state,
        initStarted: true,
      };
    }

    case 'init': {
      // console.log(`callsStore stateReducer ${displayJson(action)}`);
      if (state.ready) return state;
      return {
        ...state,
        ready: true,
        initStarted: false,
        active: action.activeCall,
      };
    }

    case 'commandStart': {
      // console.log(`callsStore stateReducer ${displayJson(action)}`);
      if (state.commandPending) return state;
      return {
        ...state,
        commandPending: true,
      };
    }

    case 'commandEnd': {
      // console.log(`callsStore stateReducer ${displayJson(action)}`);
      if (!state.commandPending) return state;
      return {
        ...state,
        commandPending: false,
      };
    }

    case 'reqCall': {
      // console.log(`callsStore stateReducer ${displayJson(action)}`);
      if (!state.commandPending) return state;
      return {
        ...state,
        commandPending: false,
        active: {
          sts: 'pending',
          link: 'ip',
          csid: action.csid,
          csCrUid: action.crUid,
          reqCrUid: action.reqCrUid,
        },
      };
    }

    case 'ackCall': {
      // console.log(`callsStore stateReducer ${displayJson(action)}`);
      if (!state.commandPending) return state;
      return {
        ...state,
        commandPending: false,
        active: {
          ...action.call,
          csid: action.csid,
          csCrUid: action.crUid,
          ackCrUid: action.crUid,
          sts: 'on',
        },
      };
    }

    case 'endCall': {
      // console.log(`callsStore stateReducer ${displayJson(action)}`);
      if (!state.commandPending) return state;
      return {
        ...state,
        commandPending: false,
        active: null,
      };
    }

    default:
      throw new Error(`CallsStore stateReducer action=${displayJson(action)}`);
  }
};

function CallsProvider(props) {
  const {
    children,
    log,
  } = props;
  const { auth } = useAuth();
  const {
    cstmrPost,
    info,
  } = auth;
  const {
    cuid,
    rlid,
  } = info;
  const [state, dispatch] = useReducer(stateReducer(log), initialState);
  const isMountedRef = useIsMounted();

  const callStatus = useCallback(
    async () => {
      let error;
      let res;
      try {
        // console.log(`callStatus at ${url}`);

        res = await cstmrPost({
          cuid,
          rlid,
          urlPath: config.town.back.calls.status,
          payload: null,
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        if (isMountedRef.current) {
          // console.log(`callStatus res.body.rslt=${displayJson(res.body.rslt, 4)}`);
          return dispatch({
            type: 'init',
            activeCall: res.body.rslt.data[0],
          });
          // typical answer:
          // CallControls calls.active = {
          //   "csid": "hKsS4UPIQqQce0ObLFyJ",
          //   "csCrUid": 33,
          //   "sts": "on",
          //   "link": "ip",
          //   "ackCrUid": 33,
          //   "gcatCode": "rescue",
          //   "reqCrUid" : 32,
          // }
        }
        // console.log(`callStatus res.body.rslt=${
        //   displayJson(res.body.rslt, 4)} returned too late, ignored`);
        return dispatch({ type: 'init' });
      }
      logError(
        `callStatus Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'CallsProvider.callStatus.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
          ...(res?.status && { resStatus: res.status }),
        },
      );
      return dispatch({ type: 'init' });
    },
    [isMountedRef, cstmrPost, cuid, rlid],
  );

  // Re-load active call upon init
  //
  useEffect(
    () => {
      if ((!state.ready) && (!state.initStarted)) {
        dispatch({ type: 'initStart' });
        // let activeCallCrUid;

        // console.log(`callsStore allCases=${displayJson(allCases)}`);
        // Use void to make it clear we do not want to wait this async function:
        void callStatus();
      }
    },
    [callStatus, state.initStarted, state.ready],
  );

  const callStart = useCallback(
    async (csid, reqCrUid, tgtCrUid) => {
      let error;
      let res;
      try {
        // console.log(`callEvent csid=${csid} at ${url}`);

        res = await cstmrPost({
          cuid,
          rlid,
          urlPath: config.town.back.cases.call.init,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            reqCrUid,
            event: 'req',
            link: 'ip',
            tgtCrUid,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`callEvent res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        return true;
      }
      logError(
        `callEvent Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'CallsProvider.callStart.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
          csid,
          reqCrUid,
          tgtCrUid,
        },
      );
      return false;
    },
    [cstmrPost, cuid, rlid],
  );

  const callCancel = useCallback(
    async (csid, reqCrUid, tgtCrUid) => {
      let error;
      let res;
      try {
        // console.log(`callEvent csid=${csid} at ${url}`);

        res = await cstmrPost({
          cuid,
          rlid,
          urlPath: config.town.back.cases.call.init,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            reqCrUid,
            event: 'cancel',
            link: 'ip',
            tgtCrUid,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`callEvent res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        return true;
      }
      logError(
        `callEvent Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'CallsProvider.callCancel.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
          csid,
          reqCrUid,
          tgtCrUid,
        },
      );
      return false;
    },
    [cstmrPost, cuid, rlid],
  );

  const callEvent = useCallback(
    async (csid, call) => {
      let error;
      let res;
      const {
        reqCrUid, ackCrUid, event, link, phone, tgtCrUid, tgtGrpLbl, tgtGcatCode,
      } = call;
      try {
        // console.log(`callEvent csid=${csid} at ${url}, call=${displayJson(call)}`);

        res = await cstmrPost({
          cuid,
          rlid,
          urlPath: config.town.back.cases.call.event,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            reqCrUid,
            ackCrUid,
            event,
            link,
            phone,
            tgtCrUid,
            tgtGrpLbl,
            tgtGcatCode,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`callEvent res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        return true;
      }
      logError(
        `callEvent Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'CallsProvider.callEvent.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
          csid,
          event,
          reqCrUid,
          ackCrUid,
        },
      );
      return false;
    },
    [cstmrPost, cuid, rlid],
  );

  const start = useCallback(
    async (csid, reqCrUid, crUid) => {
      // console.log('CallsStore start');
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });
        const rslt = await callStart(
          csid,
          reqCrUid,
          crUid,
        );
        if (rslt) {
          dispatch({
            type: 'reqCall',
            reqCrUid,
            csid,
            crUid,
          });
        } else dispatch({ type: 'commandEnd' });
      }
    },
    [state.commandPending, callStart],
  );

  const pickup = useCallback(
    async (csid, crUid, call) => {
      // console.log('CallsStore pickup');
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });
        const rslt = await callEvent(
          csid,
          {
            ...call,
            event: 'ack',
            ackCrUid: crUid,
          },
        );
        if (rslt) {
          dispatch({
            type: 'ackCall',
            call,
            csid,
            crUid,
          });
        } else dispatch({ type: 'commandEnd' });
      }
    },
    [state.commandPending, callEvent],
  );

  const end = useCallback(
    async () => {
      if ((!state.commandPending) && state.active) {
        if (state.active.sts === 'on') {
          // A call is ongoing
          dispatch({ type: 'commandStart' });
          const rslt = await callEvent(
            state.active.csid,
            {
              ...state.active,
              event: 'end',
            },
          );
          if (rslt) dispatch({ type: 'endCall' });
          else dispatch({ type: 'commandEnd' });
        }
        if (state.active.sts === 'pending') {
          dispatch({ type: 'commandStart' });
          const rslt = await callCancel(
            state.active.csid,
            state.active.reqCrUid,
            state.active.csCrUid,
          );
          if (rslt) dispatch({ type: 'endCall' });
          else dispatch({ type: 'commandEnd' });
        }
      }
    },
    [state.commandPending, state.active, callEvent, callCancel],
  );

  const contextValue = useMemo(
    () => ({
      calls: {
        start,
        pickup,
        end,
        reject: () => (null),
        active: state.active,
      },
    }),
    [end, pickup, start, state.active],
  );

  return (
    <CallsContext.Provider value={contextValue}>
      {children}
    </CallsContext.Provider>
  );
}

CallsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  log: PropTypes.string,
};

export default CallsProvider;
